import { FormatNumber } from 'utils/helpers';

import { ICONE_DO_COMPORTAMENTO } from '../../icones-do-comportamento';
import { Comportamento, COMPORTAMENTOS, comportamentosVisiveis, PontosPorComportamento } from '../../perfil-calculos';
import { ContagemPorMoeda, MOEDAS, ROTULO_DA_MOEDA } from '../../pontos-por-fase';
import CabecalhoDaSecao, { ACAO_DO_CABECALHO, SetaDaAcao } from './CabecalhoDaSecao';

interface ResumoReconhecimentosProps {
  /** Pontos por comportamento do ciclo (`find/pontos_online`). */
  pontos: PontosPorComportamento;
  ehGestor: boolean;
  /** Contagens por `coin` do `find/extrato` do ciclo; ausentes enquanto ele não chegou. */
  contagens?: Partial<Record<Comportamento, ContagemPorMoeda>>;
  /** Total de reconhecimentos recebidos no ciclo (o da linha do extrato). */
  totalRecebido?: number;
  onVerTodos: () => void;
}

function quantidade(contagem?: ContagemPorMoeda) {
  const n = contagem ? MOEDAS.reduce((soma, moeda) => soma + contagem[moeda], 0) : 0;

  return n === 1 ? '1 reconhecimento' : `${n} reconhecimentos`;
}

/** "#UAU 9 · #MANDOUBEM 6 · #VALEU 8", só com as moedas que o comportamento recebeu. */
function moedas(contagem?: ContagemPorMoeda) {
  return MOEDAS.filter(moeda => (contagem?.[moeda] ?? 0) > 0)
    .map(moeda => `${ROTULO_DA_MOEDA[moeda]} ${contagem?.[moeda]}`)
    .join(' · ');
}

/** AJ2-33: os reconhecimentos do ciclo por comportamento, no lugar de "Pontos por comportamento". */
export default function ResumoReconhecimentos({
  pontos,
  ehGestor,
  contagens,
  totalRecebido,
  onVerTodos,
}: ResumoReconhecimentosProps) {
  return (
    <section aria-labelledby="titulo-resumo-reconhecimentos">
      <CabecalhoDaSecao
        id="titulo-resumo-reconhecimentos"
        titulo="Reconhecimentos"
        subtitulo={
          totalRecebido !== undefined ? `${FormatNumber(totalRecebido, false)} pontos recebidos, por comportamento` : undefined
        }
        acao={
          <button type="button" onClick={onVerTodos} className={ACAO_DO_CABECALHO}>
            Ver todos os reconhecimentos
            <SetaDaAcao />
          </button>
        }
      />
      <ul className="m-0 flex list-none flex-col gap-2.5 rounded-2xl bg-surface-0 px-6 py-[22px] shadow-jj-01">
        {comportamentosVisiveis(COMPORTAMENTOS, ehGestor, pontos).map(({ chave, rotulo }) => {
          const contagem = contagens?.[chave];
          const tipos = moedas(contagem);

          return (
            <li key={chave} className="flex flex-wrap items-center gap-3.5 rounded-xl bg-surface-100 px-5 py-3">
              {/* Os PNGs têm fundo branco: o multiply o some sobre o cinza da linha, sem mexer no desenho. */}
              <img src={ICONE_DO_COMPORTAMENTO[chave]} alt="" className="block h-[38px] w-[38px] shrink-0 object-contain mix-blend-multiply" />
              <div className="flex min-w-[200px] flex-1 flex-col gap-[3px]">
                <span className="text-sm text-content">{rotulo}</span>
                {contagens && (
                  <span className="text-xs text-content-soft">
                    {quantidade(contagem)}
                    {tipos && (
                      <>
                        {' · '}
                        <b className="font-display font-bold text-brand-blue">{tipos}</b>
                      </>
                    )}
                  </span>
                )}
              </div>
              <span className="whitespace-nowrap rounded-jj-pill bg-brand-purple-light px-[18px] py-2 font-display text-[15px] font-bold text-content">
                {FormatNumber(pontos[chave], false)} pontos
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
